import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, CheckCircle, Calendar, FileText, ExternalLink, ClipboardList, LayoutGrid, PenSquare, BookOpen, Eye, Award, Plus, Heart, Search, Sprout, Palette, Lightbulb, ChevronRight, StickyNote, Clock, LogIn, LogOut } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 6 Dimension definitions
const DIMENSIONS = [
  { key: 'cness-tasks', label: 'CNESS Tasks', icon: ClipboardList, color: '#241665', description: 'Manage your daily tasks' },
  { key: 'self-care', label: 'Self Care', icon: Heart, color: '#10B981', description: 'Wellness & mindfulness' },
  { key: 'rnd', label: 'R&D', icon: Search, color: '#6366F1', description: 'Research & Development' },
  { key: 'personal-growth', label: 'Personal Growth', icon: Sprout, color: '#F59E0B', description: 'Learning & skills' },
  { key: 'creative', label: 'Creative', icon: Palette, color: '#EC4899', description: 'Social media & content' },
  { key: 'projects', label: 'Projects', icon: Lightbulb, color: '#8B5CF6', description: 'Project management & tracking' },
];

const DashboardHome = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeTasks: 0,
    teamMembers: 0,
    dailySubmissions: 0,
    completionRate: 0
  });
  const [educatorStats, setEducatorStats] = useState({
    totalCourses: 0,
    publishedCourses: 0,
    totalViews: 0,
    totalCompletions: 0
  });
  const [recentScans, setRecentScans] = useState([]);
  const [assignedSOPs, setAssignedSOPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);

  useEffect(() => {
    if (user.role === 'educator') {
      fetchEducatorStats();
    } else {
      fetchDashboardStats();
      fetchAssignedSOPs();
      fetchTodayAttendance();
    }
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/attendance/clock-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clocked in successfully!');
      fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/attendance/clock-out`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clocked out successfully!');
      fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch tasks (paginated response)
      const tasksResponse = await axios.get(`${API_URL}/tasks`, { 
        headers,
        params: { page: 1, page_size: 1000 } // Get all tasks for stats
      });
      const tasks = tasksResponse.data.tasks || [];
      const activeTasks = tasks.filter(t => t.status !== 'completed').length;

      // Fetch users (for managers/CEO/superadmin)
      let teamMemberCount = 0;
      if (user.role !== 'team_member') {
        const usersResponse = await axios.get(`${API_URL}/users`, { headers });
        teamMemberCount = usersResponse.data.length;
      }

      // Fetch daily scans
      const today = new Date().toISOString().split('T')[0];
      const scansResponse = await axios.get(`${API_URL}/daily-scans`, { 
        headers,
        params: { from_date: today, to_date: today }
      });
      const dailySubmissions = scansResponse.data.filter(s => s.status === 'submitted').length;

      // Fetch task analytics for completion rate
      const taskAnalyticsResponse = await axios.get(`${API_URL}/analytics/tasks`, { headers });
      const completionRate = taskAnalyticsResponse.data.completion_rate || 0;

      // Fetch recent scans for display
      const recentScansResponse = await axios.get(`${API_URL}/daily-scans`, { 
        headers,
        params: { to_date: today }
      });
      setRecentScans(recentScansResponse.data.slice(0, 5));

      setStats({
        activeTasks,
        teamMembers: teamMemberCount,
        dailySubmissions,
        completionRate
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedSOPs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API_URL}/sops`, { headers });
      // Filter SOPs assigned to current user
      const userSOPs = response.data.filter(sop => sop.assigned_to === user.id);
      setAssignedSOPs(userSOPs.slice(0, 5)); // Show max 5 SOPs on dashboard
    } catch (error) {
      console.error('Failed to fetch SOPs:', error);
    }
  };

  const fetchEducatorStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API_URL}/courses/analytics/educator`, { headers });
      setEducatorStats({
        totalCourses: response.data.total_courses || 0,
        publishedCourses: response.data.published_courses || 0,
        totalViews: response.data.total_views || 0,
        totalCompletions: response.data.total_completions || 0
      });
    } catch (error) {
      console.error('Failed to fetch educator stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-home">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-home" data-testid="dashboard-home">
      <div className="welcome-section" data-testid="welcome-section">
        <h2>Welcome back, {user.full_name}!</h2>
        <p>Here's your overview for today</p>
      </div>

      {/* Attendance Clock In/Out Widget */}
      {user.role !== 'educator' && (
        <div style={{
          padding: '20px 24px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #7077FE10, #10B98110)',
          border: '1px solid var(--border-color)',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }} data-testid="attendance-widget">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#7077FE20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={24} color="#7077FE" />
            </div>
            <div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Today's Attendance</div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <span style={{ fontSize: '13px' }}>
                  <strong style={{ color: '#10B981' }}>In:</strong> {formatTime(todayAttendance?.clock_in)}
                </span>
                <span style={{ fontSize: '13px' }}>
                  <strong style={{ color: '#EF4444' }}>Out:</strong> {formatTime(todayAttendance?.clock_out)}
                </span>
                {todayAttendance?.total_hours && (
                  <span style={{ fontSize: '13px' }}>
                    <strong style={{ color: '#7077FE' }}>Hours:</strong> {todayAttendance.total_hours}h
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {!todayAttendance?.clock_in ? (
              <button
                onClick={handleClockIn}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px'
                }}
                data-testid="dashboard-clock-in-btn"
              >
                <LogIn size={16} />
                Clock In
              </button>
            ) : !todayAttendance?.clock_out ? (
              <button
                onClick={handleClockOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px'
                }}
                data-testid="dashboard-clock-out-btn"
              >
                <LogOut size={16} />
                Clock Out
              </button>
            ) : (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 20px',
                background: '#D1FAE5',
                color: '#059669',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                <CheckCircle size={16} />
                Day Complete
              </span>
            )}
            <button
              onClick={() => navigate('/dashboard/attendance')}
              style={{
                padding: '10px 16px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                fontWeight: '500',
                fontSize: '13px'
              }}
            >
              View History
            </button>
          </div>
        </div>
      )}

      {/* 6 Dimensions Cards */}
      {user.role !== 'educator' && (
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
            🎯 Your Dimensions
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
            {DIMENSIONS.map(dim => {
              const IconComponent = dim.icon;
              return (
                <div
                  key={dim.key}
                  onClick={() => navigate(`/dashboard/${dim.key}`)}
                  style={{
                    padding: '20px',
                    borderRadius: '12px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  className="dimension-card"
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: `${dim.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px'
                  }}>
                    <IconComponent size={24} style={{ color: dim.color }} />
                  </div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {dim.label}
                  </h4>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    {dim.description}
                  </p>
                  <ChevronRight 
                    size={16} 
                    style={{ 
                      position: 'absolute', 
                      right: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: 'var(--text-tertiary)',
                      opacity: 0.5
                    }} 
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Grid - Different for Educators */}
      {user.role === 'educator' ? (
        /* Educator Stats */
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stat-card card" data-testid="stat-card-courses">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
              <BookOpen size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Courses</div>
              <div className="stat-value">{educatorStats.totalCourses}</div>
            </div>
          </div>

          <div className="stat-card card" data-testid="stat-card-published">
            <div className="stat-icon" style={{ background: 'var(--color-success)' }}>
              <CheckCircle size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Published</div>
              <div className="stat-value">{educatorStats.publishedCourses}</div>
            </div>
          </div>

          <div className="stat-card card" data-testid="stat-card-views">
            <div className="stat-icon" style={{ background: 'var(--color-info)' }}>
              <Eye size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Views</div>
              <div className="stat-value">{educatorStats.totalViews}</div>
            </div>
          </div>

          <div className="stat-card card" data-testid="stat-card-completions">
            <div className="stat-icon" style={{ background: 'var(--color-warning)' }}>
              <Award size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Completions</div>
              <div className="stat-value">{educatorStats.totalCompletions}</div>
            </div>
          </div>
        </div>
      ) : (
        /* Regular User Stats */
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stat-card card" data-testid="stat-card-tasks">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
              <CheckCircle size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Active Tasks</div>
              <div className="stat-value">{stats.activeTasks}</div>
            </div>
          </div>

          {user.role !== 'team_member' && (
            <div className="stat-card card" data-testid="stat-card-team">
              <div className="stat-icon" style={{ background: 'var(--color-success)' }}>
                <Users size={24} color="white" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Team Members</div>
                <div className="stat-value">{stats.teamMembers}</div>
              </div>
            </div>
          )}

          <div className="stat-card card" data-testid="stat-card-submissions">
            <div className="stat-icon" style={{ background: 'var(--color-warning)' }}>
              <Calendar size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Today's Submissions</div>
              <div className="stat-value">{stats.dailySubmissions}</div>
            </div>
          </div>

          <div className="stat-card card" data-testid="stat-card-performance">
            <div className="stat-icon" style={{ background: 'var(--color-secondary)' }}>
              <TrendingUp size={24} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Completion Rate</div>
              <div className="stat-value">{stats.completionRate.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Educator Quick Links */}
      {user.role === 'educator' && (
        <div className="card" style={{ marginBottom: '24px', padding: '24px' }} data-testid="educator-quick-links">
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            fontWeight: '600',
            color: 'var(--text-primary)'
          }}>
            Quick Actions
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            {/* Create New Course */}
            <button
              onClick={() => navigate('/dashboard/my-courses')}
              data-testid="quick-link-create-course"
              className="quick-link-btn"
              style={{
                padding: '20px',
                backgroundColor: '#E7EDF9',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                border: 'none',
                width: '100%',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                backgroundColor: '#241665',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Plus size={22} color="white" />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>Create Course</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>Add a new course</div>
              </div>
            </button>

            {/* View My Courses */}
            <button
              onClick={() => navigate('/dashboard/my-courses')}
              data-testid="quick-link-my-courses"
              className="quick-link-btn"
              style={{
                padding: '20px',
                backgroundColor: '#E7EDF9',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                border: 'none',
                width: '100%',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                backgroundColor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <BookOpen size={22} color="white" />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>My Courses</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>Manage your courses</div>
              </div>
            </button>

            {/* Browse All Courses */}
            <button
              onClick={() => navigate('/dashboard/courses')}
              data-testid="quick-link-browse-courses"
              className="quick-link-btn"
              style={{
                padding: '20px',
                backgroundColor: '#E7EDF9',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                border: 'none',
                width: '100%',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                backgroundColor: '#6366F1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Eye size={22} color="white" />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>Browse Courses</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>View all courses</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Regular User Quick Links - Hidden for Educators */}
      {user.role !== 'educator' && (
      <div className="card" style={{ marginBottom: '24px', padding: '24px' }} data-testid="quick-links-section">
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          fontWeight: '600',
          color: 'var(--text-primary)'
        }}>
          Quick Actions
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {/* Submit Daily Scan */}
          <button
            onClick={() => navigate('/dashboard/daily-scan')}
            data-testid="quick-link-daily-scan"
            className="quick-link-btn"
            style={{
              padding: '20px',
              backgroundColor: '#E7EDF9',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: '#241665',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <PenSquare size={22} color="white" />
            </div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>Submit Daily Scan</div>
              <div style={{ fontSize: '13px', color: '#64748B' }}>Log your daily progress</div>
            </div>
          </button>

          {/* View Assigned Tasks */}
          <button
            onClick={() => navigate('/dashboard/task-view')}
            data-testid="quick-link-task-view"
            className="quick-link-btn"
            style={{
              padding: '20px',
              backgroundColor: '#E7EDF9',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <ClipboardList size={22} color="white" />
            </div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>View Assigned Tasks</div>
              <div style={{ fontSize: '13px', color: '#64748B' }}>Check your task assignments</div>
            </div>
          </button>

          {/* View Hive Board */}
          <button
            onClick={() => navigate('/dashboard/hive-board')}
            data-testid="quick-link-hive-board"
            className="quick-link-btn"
            style={{
              padding: '20px',
              backgroundColor: '#FEF3C7',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: '#F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '22px'
            }}>
              🐝
            </div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>View Hive Board</div>
              <div style={{ fontSize: '13px', color: '#64748B' }}>Tasks by dimensions & projects</div>
            </div>
          </button>

          {/* Whiteboard */}
          <button
            onClick={() => navigate('/dashboard/whiteboard')}
            data-testid="quick-link-whiteboard"
            className="quick-link-btn"
            style={{
              padding: '20px',
              backgroundColor: '#FEF3C7',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              backgroundColor: '#D97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <StickyNote size={22} color="white" />
            </div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>Whiteboard</div>
              <div style={{ fontSize: '13px', color: '#64748B' }}>Projects & meeting notes</div>
            </div>
          </button>
        </div>
      </div>
      )}

      {/* Assigned SOPs Section */}
      {assignedSOPs.length > 0 && (
        <div className="assigned-sops card" data-testid="assigned-sops" style={{
          marginBottom: '24px',
          padding: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              margin: 0, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              <FileText size={24} color="#7077FE" />
              My Assigned SOPs
            </h3>
            <button
              className="gradient-btn"
              onClick={() => navigate('/dashboard/sops')}
            >
              View All <ExternalLink size={16} />
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {assignedSOPs.map((sop) => {
              const isTruncated = sop.description.length > 500;
              const displayDescription = isTruncated 
                ? sop.description.substring(0, 500) + '...' 
                : sop.description;
              
              return (
                <div 
                  key={sop.id} 
                  style={{
                    padding: '16px',
                    background: '#f5f7fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#7077FE';
                    e.currentTarget.style.background = '#f0f2ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.background = '#f5f7fa';
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        {sop.title}
                      </h4>
                      <p style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '14px', 
                        color: '#666',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word'
                      }}>
                        {displayDescription}
                      </p>
                      {isTruncated && (
                        <button
                          onClick={() => navigate(`/dashboard/sops/${sop.id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#7077FE',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '4px 0',
                            textDecoration: 'underline',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#5056d6'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#7077FE'}
                        >
                          Read More →
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/dashboard/sops/${sop.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <ExternalLink size={18} color="#7077FE" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {assignedSOPs.length >= 5 && (
            <div style={{ 
              marginTop: '16px', 
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Showing 5 of your SOPs. Click "View All" to see more.
            </div>
          )}
        </div>
      )}

      {user.role !== 'team_member' && recentScans.length > 0 && (
        <div className="recent-activity card" data-testid="recent-activity">
          <h3>Recent Team Submissions</h3>
          <div className="activity-list">
            {recentScans.map((scan) => (
              <div key={scan.id} className="activity-item">
                <div className="activity-date">{new Date(scan.date).toLocaleDateString()}</div>
                <div className="activity-info">
                  <span className="activity-user">Team member submitted daily scan</span>
                  <span className={`activity-mood mood-${scan.mood}`}>{scan.mood.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="quick-actions card" data-testid="quick-actions">
        <h3>Quick Actions</h3>
        <p>Navigate to different sections using the sidebar menu to get started.</p>
      </div>
    </div>
  );
};

export default DashboardHome;